import { computed, watch } from 'vue'
import type { Ref } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { useI18n } from 'vue-i18n'
import { useRpQuery } from '~/composables/graphql'
import { graphql } from '~/resources/graphql'
import { useActiveFacility } from '~/composables/use-active-facility'
import { useCurrentDate } from '~/composables/use-current-date'
import type { Language } from '~/resources/graphql/graphql'
import { onBeforeRouteUpdate } from '#app'

type PurchaseAgreementInput = {
    language: Language
    planId: string
}

export function usePurchaseAgreement(
    purchaseAgreementInput: Ref<PurchaseAgreementInput | null | undefined>
) {
    const query = useRpQuery()
    const activeFacility = useActiveFacility()
    const activeFacilityId = computed(() => activeFacility.value.id)
    const currentDate = useCurrentDate()
    const reactiveDate = computed(() => currentDate.value.getDate())
    const { locale } = useI18n({ useScope: 'global' })

    function resetAgreementLocale() {
        // Agreement pages temporarily override the global locale, so restore
        // the storefront default when this parent stops owning the flow.
        if (locale.value === 'en-US') {
            return
        }

        locale.value = 'en-US'
    }

    function syncAgreementLocale(nextLocale: 'en-US' | 'es-MX') {
        if (locale.value === nextLocale) {
            return
        }

        locale.value = nextLocale
    }

    watch(
        purchaseAgreementInput,
        nextPurchaseAgreementInput => {
            // `undefined` means the bootstrap query is still loading, while
            // `null` means the current route no longer has a usable agreement.
            if (
                nextPurchaseAgreementInput === undefined ||
                nextPurchaseAgreementInput === null
            ) {
                return
            }

            const nextLocale =
                nextPurchaseAgreementInput.language === 'SPANISH_MX'
                    ? 'es-MX'
                    : 'en-US'

            syncAgreementLocale(nextLocale)
        },
        {
            immediate: true
        }
    )

    const { data } = useQuery({
        queryKey: [
            'PurchaseAgreementQuery',
            purchaseAgreementInput,
            activeFacilityId,
            reactiveDate
        ],
        queryFn: () => {
            if (
                purchaseAgreementInput.value === undefined ||
                purchaseAgreementInput.value === null
            ) {
                return null
            }

            return query(
                graphql(/* GraphQL */ `
                    query PurchaseAgreementQuery(
                        $planId: ID!
                        $facilityId: ID!
                        $input: SimpleSpokeInput!
                        $language: Language!
                    ) {
                        plan(id: $planId) {
                            ...PurchaseAgreementPlanFragment
                        }
                    }
                `),
                {
                    planId: purchaseAgreementInput.value.planId,
                    facilityId: activeFacilityId.value,
                    input: {
                        facilityId: activeFacilityId.value
                    },
                    language: purchaseAgreementInput.value.language
                }
            )
        }
    })

    const plan = computed(() => {
        if (purchaseAgreementInput.value === undefined) {
            return undefined
        }

        if (purchaseAgreementInput.value === null) {
            return null
        }

        if (data.value === undefined) {
            return undefined
        }

        if (data.value === null) {
            return null
        }

        return data.value.plan
    })

    onBeforeRouteUpdate(() => {
        resetAgreementLocale()
    })

    return {
        plan
    }
}
